import { Box, Button, Card, Center, Flex, Heading, Spinner, Text, VStack } from '@chakra-ui/react'
import { useOktaAuth } from '@okta/okta-react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import hivemqLogo from '~/assets/03-hivemq-vert.svg'

export const Route = createFileRoute('/login')({
  component: LoginPage,
})

function LoginPage() {
  const { oktaAuth, authState } = useOktaAuth()
  const navigate = useNavigate()
  const [loginError, setLoginError] = useState<string | null>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)

  // Redirect if already authenticated
  useEffect(() => {
    if (authState?.isAuthenticated) {
      navigate({ to: '/' })
    }
  }, [authState?.isAuthenticated, navigate])

  const handleOktaLogin = async () => {
    setLoginError(null)
    setIsRedirecting(true)
    try {
      await oktaAuth.signInWithRedirect({ originalUri: '/' })
    } catch (err) {
      console.error('Okta login error:', err)
      setLoginError('Failed to initiate login. Please try again.')
      setIsRedirecting(false)
    }
  }

  if (!authState || authState.isPending) {
    return (
      <Flex h="100vh" align="center" justify="center" bg="gray.900">
        <VStack gap={4}>
          <Center>
            <Box w="200px">
              <img src={hivemqLogo} alt="HiveMQ" />
            </Box>
          </Center>
          <Spinner size="lg" color="yellow.300" />
          <Text color="white">Loading...</Text>
        </VStack>
      </Flex>
    )
  }

  if (isRedirecting) {
    return (
      <Flex h="100vh" align="center" justify="center" bg="gray.900">
        <VStack gap={4}>
          <Box w="200px">
            <img src={hivemqLogo} alt="HiveMQ" />
          </Box>
          <Spinner size="lg" color="yellow.300" />
          <Text color="white">Redirecting to Okta...</Text>
        </VStack>
      </Flex>
    )
  }

  return (
    <Flex h="100vh" align="center" justify="center" bg="gray.900">
      <Card.Root w="400px" p={8}>
        <VStack gap={6}>
          <Box w="200px">
            <img src={hivemqLogo} alt="HiveMQ" />
          </Box>

          <VStack gap={2} textAlign="center">
            <Heading size="lg">Stories Portal</Heading>
            <Text color="gray.500">Sign in to continue</Text>
          </VStack>

          {loginError && (
            <Box
              w="100%"
              p={3}
              bg="red.50"
              borderRadius="md"
              borderWidth="1px"
              borderColor="red.200"
              _dark={{ bg: 'red.900', borderColor: 'red.700' }}
            >
              <Text color="red.600" fontSize="sm" _dark={{ color: 'red.300' }}>
                {loginError}
              </Text>
            </Box>
          )}

          <Button w="100%" colorPalette="yellow.300" size="lg" onClick={handleOktaLogin}>
            Sign in with Okta
          </Button>

          <Text fontSize="xs" color="gray.400" textAlign="center">
            By signing in, you agree to the terms of service and privacy policy.
          </Text>
        </VStack>
      </Card.Root>
    </Flex>
  )
}
